<template>
  <div>
    <!-- Launcher -->
    <transition name="chat-launcher">
      <button
        v-if="!open"
        type="button"
        @click="openPanel"
        :aria-label="ct('chat_launcher')"
        class="fixed bottom-6 end-6 z-[400] w-14 h-14 rounded-full bg-[var(--color-primary)] text-white shadow-lg flex items-center justify-center hover:bg-[var(--color-primary-dark)] transition-colors focus:outline-none focus:ring-4 focus:ring-[var(--color-primary-a30)]"
      >
        <MessageCircle class="w-6 h-6" />
        <span v-if="showPulse" class="absolute -top-0.5 -end-0.5 flex h-3.5 w-3.5">
          <span class="chat-ping absolute inline-flex h-full w-full rounded-full bg-[var(--color-accent-bright)] opacity-75"></span>
          <span class="relative inline-flex rounded-full h-3.5 w-3.5 bg-[var(--color-accent-bright)] border-2 border-white"></span>
        </span>
      </button>
    </transition>

    <!-- Panel — outer box follows the SITE direction for corner placement so it
         stays aligned with the launcher; the inner wrapper carries the CHAT
         direction so the widget content is RTL-correct under Arabic even when it
         overrides the site locale. -->
    <transition name="chat-panel">
      <div
        v-if="open"
        class="fixed inset-0 sm:inset-auto sm:bottom-6 sm:end-6 z-[401] w-full h-full sm:w-[380px] sm:h-[560px] sm:max-h-[calc(100vh-3rem)] bg-white sm:rounded-2xl shadow-2xl border border-[#e2e8f0] overflow-hidden"
        role="dialog"
        aria-modal="true"
        :aria-label="ct('chat_title')"
      >
        <div :dir="chatDir" class="relative flex flex-col h-full">
          <!-- Header -->
          <div class="flex items-center gap-1.5 px-3.5 py-3 bg-[var(--color-primary)] text-white flex-shrink-0">
            <div class="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
              <Bot class="w-5 h-5" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-sm leading-tight truncate flex items-center gap-1.5">
                <span class="truncate">{{ ct('chat_title') }}</span>
                <span v-if="demoMode" :title="ct('chat_disclaimer')"
                  class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-white/20">demo</span>
              </p>
              <p class="text-[11px] text-white/80 truncate">{{ ct('chat_subtitle') }}</p>
            </div>

            <!-- Language selector -->
            <div class="relative flex-shrink-0" ref="langRef">
              <button type="button" @click="langOpen = !langOpen" :aria-label="ct('chat_lang')" :title="ct('chat_lang')"
                class="p-1.5 rounded-lg hover:bg-white/15 transition-colors">
                <Globe class="w-4 h-4" />
              </button>
              <div v-show="langOpen"
                class="absolute end-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-[#e2e8f0] z-50 py-1 overflow-hidden">
                <button v-for="l in langs" :key="l.code" type="button" @click="setChatLocale(l.code)"
                  class="w-full flex items-center gap-3 px-3 py-2 text-sm text-[#4a5568] hover:bg-[var(--color-primary-light)] hover:text-[var(--color-primary)] transition-colors"
                  :class="{ 'bg-[var(--color-primary-light)] text-[var(--color-primary)] font-medium': l.code === chatLocale }">
                  <span class="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" :class="l.bg">{{ l.flag }}</span>
                  <span class="truncate">{{ ct(l.labelKey) }}</span>
                </button>
              </div>
            </div>

            <!-- Inquiry (leave-your-details) — persistent affordance -->
            <button type="button" @click="openInquiry" :aria-label="ct('chat_inquiry_cta')" :title="ct('chat_inquiry_cta')"
              class="p-1.5 rounded-lg hover:bg-white/15 transition-colors flex-shrink-0">
              <ClipboardList class="w-4 h-4" />
            </button>

            <button type="button" @click="clearConversation" :aria-label="ct('chat_clear')" :title="ct('chat_clear')"
              class="p-1.5 rounded-lg hover:bg-white/15 transition-colors flex-shrink-0">
              <RotateCcw class="w-4 h-4" />
            </button>
            <button type="button" @click="closePanel" :aria-label="ct('chat_close')" :title="ct('chat_close')"
              class="p-1.5 rounded-lg hover:bg-white/15 transition-colors flex-shrink-0">
              <X class="w-4 h-4" />
            </button>
          </div>

          <!-- Messages -->
          <div ref="scrollEl" class="flex-1 overflow-y-auto px-3.5 py-4 bg-[#f7fafc] flex flex-col gap-3">
            <!-- Empty state + quick chips -->
            <div v-if="!messages.length" class="flex flex-col items-center text-center gap-4 my-auto py-6">
              <div class="w-12 h-12 rounded-2xl bg-[var(--color-primary-light)] flex items-center justify-center text-[var(--color-primary)]">
                <Bot class="w-6 h-6" />
              </div>
              <p class="text-sm text-[#4a5568] px-4">{{ ct('chat_subtitle') }}</p>
              <div class="grid grid-cols-1 gap-2 w-full px-2">
                <button v-for="chip in quickChips" :key="chip.key" type="button" @click="send(chip.text)"
                  class="text-start px-3 py-2 rounded-xl bg-white border border-[#e2e8f0] text-sm text-[#2d3748] hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors">
                  {{ chip.text }}
                </button>
                <button type="button" @click="openInquiry"
                  class="text-start px-3 py-2.5 rounded-xl bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-dark)] transition-colors flex items-center gap-2">
                  <ClipboardList class="w-4 h-4 flex-shrink-0" />
                  <span>{{ ct('chat_inquiry_cta') }}</span>
                </button>
              </div>
            </div>

            <!-- Message list -->
            <template v-for="m in messages" :key="m.id">
              <!-- System nav toast line -->
              <p v-if="m.role === 'system'" class="text-[11px] text-[#718096] text-center px-4 py-1">
                {{ m.content }}
              </p>

              <!-- Product card -->
              <div v-else-if="m.productId" class="self-start max-w-[90%] w-full">
                <ProductCard :product-id="m.productId" />
              </div>

              <!-- DIY draft card -->
              <div v-else-if="m.draft" class="self-start max-w-[92%] w-full">
                <DiyDraftCard :draft="m.draft" @attached="closePanel" />
              </div>

              <!-- Text bubble -->
              <div v-else
                :class="['max-w-[85%] px-3.5 py-2 rounded-2xl text-sm break-words',
                  m.role === 'user'
                    ? 'self-end bg-[var(--color-primary)] text-white rounded-br-sm'
                    : 'self-start bg-white border border-[#e2e8f0] text-[#2d3748] rounded-bl-sm']">
                <span v-if="m.role === 'user'" class="whitespace-pre-wrap">{{ m.content }}</span>
                <div v-else class="chat-md" v-html="renderMd(m.content)"></div>
              </div>
            </template>

            <!-- Typing indicator -->
            <div v-if="loading" class="self-start bg-white border border-[#e2e8f0] rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2">
              <span class="chat-dot"></span><span class="chat-dot"></span><span class="chat-dot"></span>
              <span class="text-[11px] text-[#718096] ms-1">{{ ct('chat_typing') }}</span>
            </div>

            <!-- Error line -->
            <p v-if="error" class="self-start text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              {{ ct('chat_error') }}
            </p>
          </div>

          <!-- Footer: offline notice OR input, then disclaimer -->
          <div class="flex-shrink-0 border-t border-[#e2e8f0] bg-white">
            <!-- Offline -->
            <div v-if="offline" class="p-3.5 space-y-2.5">
              <div class="flex items-start gap-2 text-sm text-[#4a5568]">
                <CloudOff class="w-4 h-4 mt-0.5 flex-shrink-0 text-[#718096]" />
                <span>{{ ct('chat_offline') }}</span>
              </div>
              <button type="button" @click="goQuote"
                class="w-full py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-dark)] transition-colors">
                {{ ct('chat_offline_cta') }}
              </button>
            </div>

            <!-- Input -->
            <div v-else class="p-2.5 relative">
              <!-- @-mention popover: search the inquiry cart and insert @orderNo.
                   The cart snapshot already rides along with every chat turn, so
                   inserting the order number as text is all the model needs. -->
              <div v-if="mentionOpen"
                class="absolute bottom-full start-2.5 end-2.5 mb-1 bg-white border border-[#e2e8f0] rounded-xl shadow-lg max-h-56 overflow-auto z-10 py-1"
                role="listbox" :aria-label="ct('quote_mention_title')">
                <p class="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-wide text-[#94a3b8] border-b border-[#f1f5f9]">
                  {{ ct('quote_mention_title') }}
                </p>
                <template v-if="mentionMatches.length">
                  <button v-for="(it, i) in mentionMatches" :key="it.orderNo" type="button" role="option"
                    :aria-selected="i === mentionActive"
                    @mousedown.prevent="pickMention(it)"
                    @mousemove="mentionActive = i"
                    :class="['w-full flex items-center gap-2.5 px-3 py-2 text-start transition-colors',
                      i === mentionActive ? 'bg-[var(--color-primary-light)]' : 'hover:bg-[#f7fafc]']">
                    <img :src="it.thumbnail" alt="" loading="lazy" decoding="async"
                      class="w-8 h-8 rounded-md object-cover bg-[#f7fafc] border border-[#e2e8f0] flex-shrink-0"
                      @error="$event.target.style.visibility = 'hidden'" />
                    <span class="min-w-0 flex-1">
                      <span class="block text-xs font-medium text-[#2d3748] truncate">{{ mentionLabel(it) }}</span>
                      <span class="block text-[11px] font-mono text-[#718096] truncate" dir="ltr">{{ it.orderNo }}</span>
                    </span>
                    <span class="text-[10px] px-1.5 py-0.5 rounded bg-[#f1f5f9] text-[#64748b] flex-shrink-0">
                      {{ it.kind === 'diy' ? ct('cart_kind_diy') : ct('cart_kind_catalog') }}
                    </span>
                  </button>
                </template>
                <p v-else class="px-3 py-2 text-xs text-[#94a3b8]">{{ ct('quote_mention_none') }}</p>
              </div>
              <div class="flex items-end gap-2">
                <textarea
                  ref="inputEl"
                  v-model="input"
                  :placeholder="ct('chat_placeholder')"
                  rows="1"
                  @keydown="onComposerKeydown"
                  @input="onComposerInput"
                  @click="updateMention"
                  class="flex-1 resize-none max-h-24 px-3 py-2 rounded-xl border border-[#e2e8f0] text-sm outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-a10)] transition-all"
                ></textarea>
                <button type="button" @click="send()" :disabled="!input.trim() || loading"
                  :aria-label="ct('chat_send')"
                  class="w-9 h-9 flex-shrink-0 rounded-xl bg-[var(--color-primary)] text-white flex items-center justify-center hover:bg-[var(--color-primary-dark)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  <Send class="w-4 h-4" :class="isRtl ? '-scale-x-100' : ''" />
                </button>
              </div>
            </div>

            <!-- Disclaimer -->
            <p class="px-3.5 pb-2 text-[11px] text-[#a0aec0] leading-tight">{{ ct('chat_disclaimer') }}</p>
          </div>

          <!-- In-chat inquiry form (modal sheet within the panel) -->
          <InquiryForm
            v-if="showInquiryForm"
            :chat-locale="chatLocale"
            @submitted="onInquirySubmitted"
            @close="showInquiryForm = false"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { MessageCircle, Bot, X, RotateCcw, Send, CloudOff, Globe, ClipboardList } from 'lucide-vue-next'
import ProductCard from './ProductCard.vue'
import DiyDraftCard from './DiyDraftCard.vue'
import InquiryForm from './InquiryForm.vue'
import {
  sendChat, checkHealth, loadHistory, saveHistory, clearHistory
} from '../../services/chatClient'
import { cartSnapshot, cartCount, cartItems } from '../../services/cart'
import { getFittingName } from '../../i18n/fittingNames'
import { productsData } from '../../data/products'
import { isAllowedRoute, routeLabel } from '../../services/navWhitelist'
import { applyPrefill } from '../../services/quoteDraft'
import { renderMarkdownLite } from '../../services/mdLite'

// Assistant replies may contain light markdown (**bold**, - lists, `code`).
// renderMarkdownLite escapes first, then emits only a fixed whitelist of tags,
// so binding it with v-html is XSS-safe. User messages stay plain text.
const renderMd = renderMarkdownLite

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n({ useScope: 'global' })

const open = ref(false)
const loading = ref(false)
const error = ref(false)
const offline = ref(false)
const demoMode = ref(false)
const input = ref('')
const hasOpened = ref(false)
const scrollEl = ref(null)
const inputEl = ref(null)
const showInquiryForm = ref(false)

// --- widget chat locale ------------------------------------------------------
// The widget keeps its OWN locale (defaults to the site locale). Once the user
// picks one from the in-widget selector we honour it; until then we follow the
// site locale. Persisted for the tab session.
const CHAT_LOCALE_KEY = 'xs-chat-locale'
const storedLocale = (() => {
  try { return sessionStorage.getItem(CHAT_LOCALE_KEY) } catch (e) { return null }
})()
const chatLocale = ref(storedLocale || locale.value)
const chatOverridden = ref(!!storedLocale)
const langOpen = ref(false)
const langRef = ref(null)

// flags for zh/ar written as unicode escapes so the source stays ASCII
const langs = [
  { code: 'zh-CN', labelKey: 'langZh', flag: '\u4e2d', bg: 'bg-red-500' },
  { code: 'en', labelKey: 'langEn', flag: 'EN', bg: 'bg-blue-600' },
  { code: 'fr', labelKey: 'langFr', flag: 'FR', bg: 'bg-indigo-500' },
  { code: 'de', labelKey: 'langDe', flag: 'DE', bg: 'bg-zinc-700' },
  { code: 'it', labelKey: 'langIt', flag: 'IT', bg: 'bg-green-700' },
  { code: 'es', labelKey: 'langEs', flag: 'ES', bg: 'bg-amber-600' },
  { code: 'tr', labelKey: 'langTr', flag: 'TR', bg: 'bg-red-700' },
  { code: 'ar', labelKey: 'langAr', flag: '\u0639', bg: 'bg-green-600' }
]

const chatDir = computed(() => (chatLocale.value === 'ar' ? 'rtl' : 'ltr'))
const isRtl = computed(() => chatLocale.value === 'ar')

// Translate the widget's own UI in the chat locale (overrides the site locale).
const ct = (key, named) => t(key, named || {}, { locale: chatLocale.value })

// Follow the site locale until the user overrides it in the widget.
watch(locale, (v) => { if (!chatOverridden.value) chatLocale.value = v })

function setChatLocale(code) {
  chatLocale.value = code
  chatOverridden.value = true
  try { sessionStorage.setItem(CHAT_LOCALE_KEY, code) } catch (e) { /* ignore */ }
  langOpen.value = false
}

const messages = ref(loadHistory())

// Persist across route changes / reloads within the tab.
watch(messages, (val) => saveHistory(val), { deep: true })

const showPulse = computed(() => !hasOpened.value && !messages.value.length)

const quickChips = computed(() => {
  const chips = [
    { key: 'recommend', text: ct('chat_quick_recommend') },
    { key: 'specs', text: ct('chat_quick_specs') },
    { key: 'quote', text: ct('chat_quick_quote') },
    { key: 'diy', text: ct('chat_quick_diy') }
  ]
  // Surface a cart-aware chip only when there's something to ask about.
  if (cartCount.value > 0) chips.push({ key: 'cart', text: ct('cart_ask_ai') })
  return chips
})

let idSeq = 0
function nextId() {
  idSeq += 1
  return Date.now().toString(36) + '-' + idSeq
}
function pushMessage(m) {
  messages.value.push({ id: nextId(), ...m })
  scrollToBottom()
}

function scrollToBottom() {
  nextTick(() => {
    if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight
  })
}

async function openPanel() {
  open.value = true
  hasOpened.value = true
  error.value = false
  scrollToBottom()
  // Health probe — never throws. `mock` true means the backend serves canned
  // demo replies (no key configured): not offline, but flagged honestly.
  const health = await checkHealth()
  demoMode.value = !!health.ok && !!health.mock
  offline.value = !health.ok || (!health.hasKey && !health.mock)
  if (!offline.value && !showInquiryForm.value) {
    nextTick(() => { if (inputEl.value) inputEl.value.focus() })
  }
}

function closePanel() {
  open.value = false
  langOpen.value = false
  showInquiryForm.value = false
}

function openInquiry() {
  langOpen.value = false
  showInquiryForm.value = true
}

function autoGrow(e) {
  const el = e.target
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 96) + 'px'
}

// --- @-mention over inquiry-cart orders --------------------------------------
// Typing '@' (at start or after whitespace) opens a popover that live-filters
// the cart by order number / product / fitting / size. Picking an entry inserts
// "@XS-...." at the caret; the model resolves it via the cart snapshot that
// already accompanies every turn.
const mentionOpen = ref(false)
const mentionActive = ref(0)
const mentionStart = ref(-1)
const mentionQ = ref('')

function productLabel(id) {
  const p = productsData.find((x) => x.id === id)
  return p ? (p.name[chatLocale.value] || p.name.en) : id
}

function mentionLabel(it) {
  const prod = productLabel(it.productId)
  if (it.kind === 'diy') {
    const t = it.diy && it.diy.fittingType ? it.diy.fittingType : ct('cart_kind_diy')
    return prod + ' · ' + t
  }
  const fit = it.fittingKey ? getFittingName(it.fittingKey, chatLocale.value) : ct('cart_whole_line')
  return prod + ' · ' + fit + (it.size ? ' · ' + it.size : '')
}

// Locate an active "@token" ending at the caret. Returns null when the caret
// is not inside one (also guards against email-like mid-word '@').
function mentionQueryInfo() {
  const el = inputEl.value
  const caret = el ? el.selectionStart : input.value.length
  const text = input.value
  const at = text.lastIndexOf('@', caret - 1)
  if (at === -1) return null
  if (at > 0 && !/\s/.test(text[at - 1])) return null
  const q = text.slice(at + 1, caret)
  if (/\s/.test(q)) return null
  return { at, caret, q }
}

function updateMention() {
  if (!cartCount.value) { mentionOpen.value = false; return }
  const info = mentionQueryInfo()
  if (!info) { mentionOpen.value = false; return }
  mentionStart.value = info.at
  mentionQ.value = info.q.toLowerCase()
  if (!mentionOpen.value) mentionActive.value = 0
  mentionOpen.value = true
}

const mentionMatches = computed(() => {
  if (!mentionOpen.value) return []
  const items = cartItems.value
  const q = mentionQ.value.trim()
  const list = !q ? items : items.filter((it) => {
    const hay = (it.orderNo + ' ' + mentionLabel(it) + ' ' + (it.note || '')).toLowerCase()
    return q.split(/\s+/).every((t) => hay.includes(t))
  })
  return list.slice(0, 8)
})

watch(mentionMatches, (l) => { if (mentionActive.value >= l.length) mentionActive.value = 0 })

function pickMention(it) {
  const el = inputEl.value
  const caret = el ? el.selectionStart : input.value.length
  const before = input.value.slice(0, mentionStart.value)
  const after = input.value.slice(caret)
  const token = '@' + it.orderNo + ' '
  input.value = before + token + after
  mentionOpen.value = false
  nextTick(() => {
    if (el) {
      el.focus()
      const pos = (before + token).length
      el.setSelectionRange(pos, pos)
    }
  })
}

function onComposerInput(e) {
  autoGrow(e)
  updateMention()
}

function onComposerKeydown(e) {
  if (mentionOpen.value) {
    const n = mentionMatches.value.length
    if (e.key === 'ArrowDown' && n) { e.preventDefault(); mentionActive.value = (mentionActive.value + 1) % n; return }
    if (e.key === 'ArrowUp' && n) { e.preventDefault(); mentionActive.value = (mentionActive.value - 1 + n) % n; return }
    if ((e.key === 'Enter' || e.key === 'Tab') && n) {
      e.preventDefault(); e.stopPropagation()
      pickMention(mentionMatches.value[mentionActive.value])
      return
    }
    if (e.key === 'Escape') {
      // Close only the mention popover — stopPropagation keeps the panel's own
      // window-level Escape handler from also closing the chat.
      e.preventDefault(); e.stopPropagation()
      mentionOpen.value = false
      return
    }
  }
  if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
    e.preventDefault()
    send()
  }
}

function clearConversation() {
  messages.value = []
  clearHistory()
  error.value = false
}

function goQuote() {
  closePanel()
  router.push('/quote')
}

async function send(text) {
  const content = (typeof text === 'string' ? text : input.value).trim()
  if (!content || loading.value || offline.value) return
  pushMessage({ role: 'user', content })
  input.value = ''
  mentionOpen.value = false
  if (inputEl.value) inputEl.value.style.height = 'auto'
  error.value = false
  loading.value = true

  const apiMessages = messages.value
    .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content)
    .map((m) => ({ role: m.role, content: m.content }))
    .slice(-12)

  try {
    // Forward the current inquiry-cart snapshot so the assistant can answer
    // "what's in my cart / compare / quote these" with real context.
    const data = await sendChat({ messages: apiMessages, locale: chatLocale.value, page: route.path, cart: cartSnapshot() })
    if (data.reply) pushMessage({ role: 'assistant', content: data.reply })
    for (const action of data.actions) handleAction(action)
  } catch (e) {
    if (e && e.offline) offline.value = true
    else error.value = true
  } finally {
    loading.value = false
    scrollToBottom()
  }
}

function handleAction(action) {
  if (!action || typeof action !== 'object') return
  switch (action.type) {
    case 'navigate':
      doNavigate(action.route)
      break
    case 'show_product':
      if (action.productId) pushMessage({ role: 'assistant', productId: action.productId })
      break
    case 'diy_draft':
      if (action.draft) pushMessage({ role: 'assistant', draft: action.draft })
      break
    case 'quote_prefill':
      applyPrefill(action.fields || {})
      doNavigate('/quote')
      break
    case 'open_inquiry_form':
      openInquiry()
      break
    case 'inquiry_submitted':
      if (action.inquiryId) {
        pushMessage({ role: 'assistant', content: ct('chat_inquiry_sent', { id: action.inquiryId }) })
      }
      break
    default:
      // Unknown action type — drop silently.
      break
  }
}

// The in-chat form filed an inquiry: confirm with the reference id, echo the
// auto-reply as a plain assistant bubble, and collapse the form.
function onInquirySubmitted({ inquiryId, autoReply }) {
  showInquiryForm.value = false
  pushMessage({ role: 'assistant', content: ct('chat_inquiry_sent', { id: inquiryId }) })
  if (autoReply) pushMessage({ role: 'assistant', content: autoReply })
  scrollToBottom()
}

function doNavigate(target) {
  // Validate against the local whitelist; unknown/invalid routes are dropped.
  if (!isAllowedRoute(target)) return
  pushMessage({ role: 'system', content: ct('chat_nav_going', { page: routeLabel(target, ct, chatLocale.value) }) })
  setTimeout(() => {
    if (route.path !== target) router.push(target)
  }, 600)
}

function onClickOutside(e) {
  if (langRef.value && !langRef.value.contains(e.target)) langOpen.value = false
}

// Escape closes the dialog for keyboard users. Listener is only attached
// while the panel is open and is torn down on close/unmount.
function onKeydown(e) {
  if (e.key === 'Escape') closePanel()
}
watch(open, (v) => {
  if (v) document.addEventListener('keydown', onKeydown)
  else document.removeEventListener('keydown', onKeydown)
})

// Close the panel whenever we navigate (nav action, product view, quote attach).
watch(() => route.fullPath, () => { closePanel() })

// The cart drawer's "ask the assistant" button dispatches this window event:
// open the panel and ask a localized cart-summary question so the bot (which
// receives the cart snapshot on every send) summarizes / compares the cart.
async function onOpenChatCart() {
  const question = ct('cart_ask_ai')
  if (!open.value) await openPanel()
  if (offline.value) {
    // Backend unreachable — seed the input instead of dropping the request.
    input.value = question
    nextTick(() => { if (inputEl.value) inputEl.value.focus() })
    return
  }
  send(question)
}

onMounted(() => {
  // Reflect prior conversation in the pulse state.
  if (messages.value.length) hasOpened.value = true
  document.addEventListener('click', onClickOutside)
  window.addEventListener('xs-open-chat-cart', onOpenChatCart)
})
onUnmounted(() => {
  document.removeEventListener('click', onClickOutside)
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('xs-open-chat-cart', onOpenChatCart)
})
</script>

<style scoped>
/* Rendered light-markdown in assistant bubbles (content injected via v-html;
   :deep is required to reach it from a scoped stylesheet). */
.chat-md :deep(strong) { font-weight: 700; }
.chat-md :deep(em) { font-style: italic; }
.chat-md :deep(code) {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85em;
  background: #eef2f7;
  padding: 0.05em 0.35em;
  border-radius: 4px;
}
.chat-md :deep(ul),
.chat-md :deep(ol) { margin: 0.35em 0; padding-inline-start: 1.25em; }
.chat-md :deep(ul) { list-style: disc; }
.chat-md :deep(ol) { list-style: decimal; }
.chat-md :deep(li) { margin: 0.15em 0; }
.chat-md :deep(li)::marker { color: var(--color-primary); }

/* Panel slide + fade */
.chat-panel-enter-active,
.chat-panel-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.chat-panel-enter-from,
.chat-panel-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
}

.chat-launcher-enter-active,
.chat-launcher-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.chat-launcher-enter-from,
.chat-launcher-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

/* Typing dots */
.chat-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: #cbd5e0;
  display: inline-block;
  animation: chat-bounce 1.2s infinite ease-in-out;
}
.chat-dot:nth-child(2) { animation-delay: 0.15s; }
.chat-dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes chat-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
  30% { transform: translateY(-4px); opacity: 1; }
}

/* Launcher pulse dot */
.chat-ping { animation: chat-ping 1.6s cubic-bezier(0, 0, 0.2, 1) infinite; }
@keyframes chat-ping {
  75%, 100% { transform: scale(2); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .chat-panel-enter-active,
  .chat-panel-leave-active,
  .chat-launcher-enter-active,
  .chat-launcher-leave-active {
    transition: opacity 0.2s ease;
  }
  .chat-panel-enter-from,
  .chat-panel-leave-to { transform: none; }
  .chat-dot,
  .chat-ping { animation: none; }
}
</style>
